import { ISpecification } from './interface.specification';

export abstract class Specification<T> implements ISpecification<T> {
	/// <summary>
	///     Determines whether the specified candidate is satisfied by T.
	/// </summary>
	/// <param name="candidate">The candidate.</param>
	/// <returns>
	///     <c>true</c> if [is satisfied by] [the specified candidate]; otherwise, <c>false</c>.
	/// </returns>
	abstract IsSatisfiedBy(candidate: T): boolean;

	/// <summary>
	///     And the specified specification.
	/// </summary>
	/// <param name="specification">The specification.</param>
	/// <returns></returns>
	And(specification: ISpecification<T>): ISpecification<T> {
		// tslint:disable-next-line: no-use-before-declare
		return new AndSpecification<T>(this, specification);
	}

	/// <summary>
	///     Ors the specified specification.
	/// </summary>
	/// <param name="specification">The specification.</param>
	/// <returns></returns>
	Or(specification: ISpecification<T>): ISpecification<T> {
		// tslint:disable-next-line: no-use-before-declare
		return new OrSpecification<T>(this, specification);
	}

	/// <summary>
	///     Not this instance.
	/// </summary>
	/// <returns></returns>
	Not(): ISpecification<T> {
		// tslint:disable-next-line: no-use-before-declare
		return new NotSpecification<T>(this);
	}
}

export abstract class CompositeSpecification<T> extends Specification<T> {
	/// <summary>
	/// </summary>
	protected readonly left: ISpecification<T>;

	/// <summary>
	/// </summary>
	protected readonly right: ISpecification<T>;

	/// <summary>
	///     Initializes a new instance of the <see cref="CompositeSpecification&lt;T&gt;" /> class.
	/// </summary>
	/// <param name="left">The left.</param>
	/// <param name="right">The right.</param>
	constructor(_left: ISpecification<T>, _right: ISpecification<T>) {
		super();
		this.left = _left;
		this.right = _right;
	}
}

export class AndSpecification<T> extends CompositeSpecification<T> {
	/// <summary>
	///     Initializes a new instance of the <see cref="AndSpecification&lt;T&gt;" /> class.
	/// </summary>
	/// <param name="left">The left.</param>
	/// <param name="right">The right.</param>
	constructor(left: ISpecification<T>, right: ISpecification<T>) {
		super(left, right);
	}

	/// <summary>
	///     Determines whether the specified candidate is satisfied by T.
	/// </summary>
	/// <param name="candidate">The candidate.</param>
	/// <returns>
	///     <c>true</c> if [is satisfied by] [the specified candidate]; otherwise, <c>false</c>.
	/// </returns>
	IsSatisfiedBy(candidate: T): boolean {
		return this.left.IsSatisfiedBy(candidate) && this.right.IsSatisfiedBy(candidate);
	}
}

/// <summary>
/// OrSpecification&lt;T&gt;
/// </summary>
/// <typeparam name="T">The type of the candidate.</typeparam>
export class OrSpecification<T> extends CompositeSpecification<T> {
	/// <summary>
	/// Initializes a new instance of the <see cref="OrSpecification&lt;T&gt;" /> class.
	/// </summary>
	/// <param name="left">The left.</param>
	/// <param name="right">The right.</param>
	constructor(left: ISpecification<T>, right: ISpecification<T>) {
		super(left, right);
	}

	/// <summary>
	/// Determines whether the specified candidate is satisfied by T.
	/// </summary>
	/// <param name="candidate">The candidate.</param>
	/// <returns>
	/// <c>true</c> if [is satisfied by] [the specified candidate]; otherwise, <c>false</c>.
	/// </returns>
	IsSatisfiedBy(candidate: T): boolean {
		return this.left.IsSatisfiedBy(candidate) || this.right.IsSatisfiedBy(candidate);
	}
}

/// <summary>
///     NotSpecification&lt;T&gt;
/// </summary>
/// <typeparam name="T">The type of the candidate.</typeparam>
export class NotSpecification<T> extends Specification<T> {
	private readonly _specification: ISpecification<T>;

	/// <summary>
	///     Initializes a new instance of the <see cref="NotSpecification&lt;T&gt;" /> class.
	/// </summary>
	/// <param name="specification">The specification.</param>
	constructor(specification: ISpecification<T>) {
		super();
		this._specification = specification;
	}

	/// <summary>
	///     Determines whether the specified candidate is satisfied by T.
	/// </summary>
	/// <param name="candidate">The candidate.</param>
	/// <returns>
	///     <c>true</c> if [is satisfied by] [the specified candidate]; otherwise, <c>false</c>.
	/// </returns>
	IsSatisfiedBy(candidate: T): boolean {
		return !this._specification.IsSatisfiedBy(candidate);
	}
}
